import type { JSX, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { button } from './look';
import { useEscape } from './use-escape';

/**
 * The 520px drawer that slides in from the right — the design's source
 * detail. On a phone it is a bottom sheet rising to 56px below the top. `header` sits beside the close button; `footer` is pinned below the
 * scrolling body.
 */
export function SidePanel({
  label,
  header,
  footer,
  onClose,
  children,
}: {
  label: string;
  header: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  useEscape(onClose);
  // Portalled to <body>: pages enter with a transform animation (`rise`, fill
  // mode both), and a transformed ancestor would pin `fixed` to the page box
  // instead of the viewport, clipping the overlay.
  return createPortal(
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-30 bg-[oklch(0_0_0/0.45)] backdrop-blur-[2px]"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="bg-surf border-line shadow-panel animate-sheet-up tab:animate-drawer fixed inset-x-0 top-14 bottom-0 z-[31] flex flex-col rounded-t-[20px] border-t tab:top-0 tab:left-auto tab:w-[min(520px,100vw)] tab:rounded-none tab:border-t-0 tab:border-l"
      >
        <div className="border-line flex items-center gap-3.5 border-b px-7 pt-6 pb-[18px]">
          <div className="flex min-w-0 flex-1 items-center gap-3.5">{header}</div>
          <button type="button" onClick={onClose} aria-label="Close" className={button.close}>
            ×
          </button>
        </div>
        <div className="flex flex-1 flex-col gap-[26px] overflow-y-auto px-7 py-6">{children}</div>
        {footer !== undefined && (
          <div className="border-line flex items-center justify-between gap-3 border-t px-7 py-[18px]">
            {footer}
          </div>
        )}
      </aside>
    </>,
    document.body,
  );
}
